import { Exercise, UserProfile, WorkoutSettings } from 'fitbeat.gym-core';
import { BaseEquipment, GymTypes, EquipmentCombinations } from 'fitbeat.models';
import React, { FunctionComponent } from 'react';
import { translate } from 'react-i18next';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { Button } from '../../../components';
import { Orientation } from '../../../HomeWorkouts';
import { HomeWorkoutResponsiveLayout } from '../../../HomeWorkouts/components/HomeWorkoutResponsiveLayout';

import BagContactGlove from '../../../assets/images/bag-contact-glove.svg';
import BagContactHand from '../../../assets/images/hand-gasture.svg';
import Theme, { ThemeType } from '../../../theme';
import { mappedSkillLevelIcons, mappedSkillLevelTitle } from '../../../ui/components/SkillLevelList';
import { getBodyImage } from '../../../utils';
import { defaultScale } from '../../../utils/scaling';
import { mappedBodyGroup } from '../../components/BodyGroupSelection';
import { mappedWorkoutType } from '../../components/WorkoutTypeSelection';

import BenchAndDumbbellsIcon from '../../../assets/images/bench-and-dumbbells.svg';
import BenchIcon from '../../../assets/images/icon-bench-outline.svg';
import BodyWeightIcon from '../../../assets/images/icon-bodyweight-outline.svg';
import DumbbellsIcon from '../../../assets/images/icon-dumbbells-outline.svg';

import { themeStyleMapper } from './PersonaliseWorkoutSetup';
import { PersonaliseSetupScreens } from './types'

interface IProps {
  themeType: ThemeType;
  orientation: Orientation;
  workoutSettings: any;
  userProfile: UserProfile;
  gym: any;
  t(key: string, options?: any): string;
  openPersonaliseSetupScreen(screen: PersonaliseSetupScreens): void;
}

const getEquipmentListIcon = (equipment, themeStyle) => {
  if (BaseEquipment.containsBench(equipment) && BaseEquipment.containsHomeDumbbells(equipment)) {
    return <BenchAndDumbbellsIcon
      fill={themeStyle.themeColor1Inverse}
      stroke={themeStyle.themeColor1}
      strokeWidth={1.1}
      width={defaultScale(38)}
      height={defaultScale(38)} />
  }
  if (BaseEquipment.containsHomeDumbbells(equipment)) {
    return <DumbbellsIcon
      fill={themeStyle.themeColor1Inverse}
      stroke={themeStyle.themeColor1}
      strokeWidth={1.1}
      width={defaultScale(38)} 
      height={defaultScale(38)} />
  }
  if (BaseEquipment.containsBodyWeigth(equipment) && equipment.length === 1) {
    return <BodyWeightIcon
      stroke={themeStyle.themeColor1}
      strokeWidth={1.1}
      width={defaultScale(38)}
      height={defaultScale(38)} />
  }
  return <BenchIcon
    stroke={themeStyle.themeColor1}
    strokeWidth={1.1}
    width={defaultScale(38)}
    height={defaultScale(38)} />
}

const PersonaliseWorkoutOverviewComponent: FunctionComponent<IProps> = ({
  t,
  themeType = ThemeType.light,
  orientation,
  workoutSettings,
  userProfile,
  gym,
  openPersonaliseSetupScreen,
}) => {
  const themeStyle = themeStyleMapper[themeType];
  const currentWorkoutSettings = new WorkoutSettings(workoutSettings);
  const { equipment = BaseEquipment.EquipmentCombinationList[EquipmentCombinations.BODYWEIGHT], threeBodyGroups, cardioOrResistance } = workoutSettings;
  const { gender } = userProfile;
  const bodyImageHeight = defaultScale(100);
  const bodyImageWidth = defaultScale(100);
  const bodyImageFill1 = themeStyle.themeColor1Inverse;
  const bodyImageFill2 = themeStyle.bodyImageColor2;
  const bodyImage = getBodyImage(threeBodyGroups, gender, cardioOrResistance, bodyImageHeight,
     bodyImageWidth, bodyImageFill1, bodyImageFill2);
  const isHomeGym = gym.type === GymTypes.Home;
  const hasBagContact = gym.bagContactAllowed;
  const isPortrait = orientation === 'PORTRAIT';
  const responsiveStyles = isPortrait ? portraitStyles : styles;
  const containerStyle = Platform.OS === 'web' ? {} : { flex: 0 , flexWrap: 'nowrap' };
  const equipmentListDisplay =  isHomeGym  && BaseEquipment.getEquipmentCombinationForDisplay(equipment);
  const equipmentListDisplayIcon = getEquipmentListIcon(equipment, themeStyle);
  const equipmentListDisplayLength = equipmentListDisplay  ? equipmentListDisplay.split(',').length : 0;
  return (
    <View style={{flex: 1, justifyContent: 'center', alignItems: 'center'}}>
    <View style = {styles.optionsHeadingContainer}>
      <Text style={[styles.optionsHeading, themeStyle.textColor1]}>{t('customiseYourWorkout')}</Text>
    </View>
    <HomeWorkoutResponsiveLayout containerStyle= {containerStyle}>
      <View style={styles.optionsRowContainer}>
        <View style={[styles.optionContainer, {marginRight: 15}]}>
          <Text style={[styles.settingsHeader, themeStyle.textColor4]}>{t('skillLevel').toUpperCase()}</Text>
            <Button
              title={mappedSkillLevelTitle[currentWorkoutSettings.level].alt}
              onPress={() => openPersonaliseSetupScreen(PersonaliseSetupScreens.SkillLevel)}
              containerStyle={[styles.overviewBtn, themeStyle.border2, styles.optionButtonSize]}
              fontColor={themeStyle.themeColor3}
              overrideFontStyles={[styles.optionButtonTitle, { color: themeStyle.themeColor3 }]}
              contentContainerStyle={{flexDirection: 'column'}}
              icon={mappedSkillLevelIcons[currentWorkoutSettings.level](themeStyle.themeColor1)}
            />
        </View>
        <View style={[styles.optionContainer]}>
          <Text style={[styles.settingsHeader, themeStyle.textColor4]}>{t('bodyGroupAlt').toUpperCase()}</Text>
            <Button
            title={mappedBodyGroup[Exercise.getCurrentBodyGroupFrom3BodyGroups(threeBodyGroups)].title}
            onPress={() => openPersonaliseSetupScreen(PersonaliseSetupScreens.BodyGroup)}
            containerStyle={[styles.overviewBtn, themeStyle.border2, styles.optionButtonSize]}
              overrideFontStyles={[styles.optionButtonTitle, { color: themeStyle.themeColor3 }, { paddingTop: 0 }]}
            contentContainerStyle={{...responsiveStyles.bodyGroupBtnContentDirection, flexDirection: 'column'}}
            fontColor={themeStyle.themeColor3}
            icon={
              <View style={styles.bodygroupImageContainer}>
                {bodyImage}
              </View>
            }
            />
        </View>
      </View>
      <View style={[styles.optionsRowContainer]}>
        {isHomeGym &&
        <View style={[styles.optionContainer, {marginRight: 15}, responsiveStyles.equipmentMargin]}>
          <Text style={[styles.settingsHeader, themeStyle.textColor4]}>{t('equipment').toUpperCase()}</Text>
            <Button
              title={equipmentListDisplay}
              onPress={() => openPersonaliseSetupScreen(PersonaliseSetupScreens.Equipment)}
              overrideFontStyles={[styles.optionButtonTitle, { color: themeStyle.themeColor3 }]}
              containerStyle={[styles.overviewBtn, themeStyle.border2, styles.optionButtonSize]}
              contentContainerStyle={{
                flexDirection: 'column',
                paddingTop: equipmentListDisplayLength > 2 ? defaultScale(16) : null,
              }}
              fontColor={themeStyle.themeColor3}
              icon={equipmentListDisplayIcon }
               />
        </View>}
        {hasBagContact && <View style={[styles.optionContainer, {marginRight: 15}]}>
          <Text style={[styles.settingsHeader,themeStyle.textColor4]}>{t('combatOrCloves').toUpperCase()}</Text>
            <Button
              title={currentWorkoutSettings.allowBagContact ? t('yes'): t('no')}
              onPress={() => openPersonaliseSetupScreen(PersonaliseSetupScreens.BagContact)}
              containerStyle={[styles.overviewBtn, themeStyle.border2, styles.optionButtonSize]}
              contentContainerStyle={{flexDirection: 'column'}}
              overrideFontStyles={[styles.optionButtonTitle, { color: themeStyle.themeColor3 }]}
              fontColor={themeStyle.themeColor3}
              icon={
                currentWorkoutSettings.allowBagContact ?
                <BagContactGlove
                  stroke={themeStyle.themeColor1}
                  strokeWidth={1.7}
                  width={defaultScale(32)}
                  height={defaultScale(32)} /> :
                <BagContactHand
                  stroke={themeStyle.themeColor1}
                  strokeWidth={1.7}
                  width={defaultScale(32)}
                  height={defaultScale(32)} />
              }
            />
        </View>}
      <View style={styles.optionContainer}>
        <Text style={[styles.settingsHeader,themeStyle.textColor4]}>{t('Type').toUpperCase()}</Text>
          <Button
            title={mappedWorkoutType[currentWorkoutSettings.cardioOrResistance].title}
            onPress={() => openPersonaliseSetupScreen(PersonaliseSetupScreens.WorkoutType)}
            containerStyle={[styles.overviewBtn, themeStyle.border2, styles.optionButtonSize]}
            contentContainerStyle={{flexDirection: 'column'}}
            overrideFontStyles={[styles.optionButtonTitle, { color: themeStyle.themeColor3 }]}
            fontColor={themeStyle.themeColor3}
            icon={mappedWorkoutType[currentWorkoutSettings.cardioOrResistance].icon(themeStyle.themeColor1)}
          />
        </View>
      </View>
    </HomeWorkoutResponsiveLayout>
    </View>
  );
};

const portraitStyles = StyleSheet.create({
  bodyGroupBtnContentDirection: {
    flexDirection: 'row',
  },
  bodygroupImageContainer: {
    marginRight: defaultScale(16),
  },
  equipmentMargin: {
    marginLeft: 0,
  },
});

const styles = StyleSheet.create({
  bodyGroupBtnContentDirection: {
    flexDirection: 'column',
  },
  settingsHeader: {
    ...Theme.textStyle.bodyCopyBold,
    color: Theme.CHARCOAL,
    paddingBottom: defaultScale(5),
  },
  overviewBtn: {
    borderColor: Theme.GREY_MEDIUM,
    borderRadius: defaultScale(5),
    backgroundColor: 'transparent',
    height: defaultScale(60),
    borderWidth: defaultScale(2),
    justifyContent: 'center',
    alignItems: 'center',
  },
  bodygroupImageContainer: {
    marginRight: 0,
  },
  optionsRowContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  optionsHeading: {
    ...Theme.textStyle.heading,
    paddingTop: defaultScale(30),
    paddingBottom:defaultScale(30),
    textAlign: 'center',
    color: Theme.GREY_DARK,
  },
  optionContainer: {
    flexDirection: 'column',
    marginVertical: defaultScale(15),
  },
  optionButtonSize: {
    height: defaultScale(150),
    width : defaultScale(150),
  },
  optionsHeadingContainer: {
    justifyContent: 'center',
  },
  optionButtonTitle: {
    ...Theme.textStyle.bodyCopyReg,
    textAlign: 'center',
    paddingTop: defaultScale(10),
  },
  equipmentMargin: {
    marginLeft: defaultScale(15),
  },
});

export const PersonaliseWorkoutOverview =
  (translate(['bookings', 'common'], { wait: true })(PersonaliseWorkoutOverviewComponent));
